import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { ProductCard } from "@/components/product-card";
import { ProductSkeleton } from "@/components/skeleton-card";
import { fetchProducts, type Product } from "@/lib/queries";
import { useEffect, useState } from "react";
import { Reveal } from "@/components/reveal";
import { FlipWords } from "@/components/flip-words";
import { TextGenerateEffect } from "@/components/text-generate-effect";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { algoliaEnabled, algoliaSearch, algoliaSearchCreators, algoliaIndexForSort } from "@/lib/search";

export const Route = createFileRoute("/discover")({
  head: () => ({
    meta: [
      { title: "Discover Products — Shop Creator Curations | Pluggz" },
      { name: "description", content: "Browse thousands of products curated by influencers you trust. Filter by category, sort by price and shop directly from creator storefronts." },
      { property: "og:title", content: "Discover Products — Shop Creator Curations | Pluggz" },
      { property: "og:description", content: "Browse thousands of products curated by influencers you trust. Filter by category, sort by price and shop directly from creator storefronts." },
      { property: "og:url", content: `${SITE_URL}/discover` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/discover` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Discover Products on Pluggz",
          description: "Browse thousands of products curated by influencers you trust. Filter by category, sort by price and shop directly from creator storefronts.",
          url: `${SITE_URL}/discover`,
        }),
      },
    ],
  }),
  component: Discover,
});

const categories = [
  "All",
  "Womens Fashion",
  "Beauty & Skincare",
  "Mother & Baby",
  "Home & Decor",
  "Fitness & Wellness",
  "Shoes & Accessories",
  "Cooking & Grocery",
  "Finance & Lifestyle",
  "Travel",
  "GRWM",
] as const;
type Cat = (typeof categories)[number];
const sorts = ["Featured", "Newest", "Price: Low", "Price: High"] as const;
type Sort = (typeof sorts)[number];

type CreatorHit = {
  objectID: string;
  handle?: string | null;
  name?: string | null;
  avatar_url?: string | null;
  bio?: string | null;
};

const PAGE_SIZE = 24;

function Discover() {
  const initialCat: Cat = (() => {
    if (typeof window === "undefined") return "All";
    const param = new URLSearchParams(window.location.search).get("category");
    return (categories as readonly string[]).includes(param ?? "") ? (param as Cat) : "All";
  })();
  const [cat, setCat] = useState<Cat>(initialCat);
  const [sort, setSort] = useState<Sort>("Featured");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);
  const { data: products = [], isLoading } = useQuery({ queryKey: ["products"], queryFn: fetchProducts });

  // 300ms debounce on the live query for Algolia
  useEffect(() => {
    if (!algoliaEnabled) return;
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [cat, sort, debounced, search]);

  const facetFilters: string[][] = [];
  if (cat !== "All") facetFilters.push([`category:${cat}`]);

  const { data: algoliaResult, isLoading: algoliaLoading, isFetching: algoliaFetching } = useQuery({
    queryKey: ["algolia-products", debounced, cat, sort, page],
    enabled: algoliaEnabled,
    queryFn: async () => {
      const result = await algoliaSearch<Product>(debounced, {
        hitsPerPage: PAGE_SIZE,
        facetFilters,
        indexName: algoliaIndexForSort(sort),
        page,
      });
      return result ?? { hits: [], nbHits: 0, nbPages: 0, page: 0 };
    },
  });

  const { data: creatorHits = [], isLoading: creatorsLoading, isFetching: creatorsFetching, error: creatorsError, refetch: refetchCreators } = useQuery({
    queryKey: ["algolia-creators", debounced],
    enabled: algoliaEnabled && debounced.length > 1,
    queryFn: async () => {
      const hits = await algoliaSearchCreators<CreatorHit>(debounced, 6);
      return hits ?? [];
    },
  });
  const creatorsLoadingOrFetching = creatorsLoading || creatorsFetching;

  function sortProducts(list: Product[]): Product[] {
    if (sort === "Price: Low") return [...list].sort((a, b) => a.price - b.price);
    if (sort === "Price: High") return [...list].sort((a, b) => b.price - a.price);
    if (sort === "Newest") return [...list].sort((a, b) => new Date((b as any).created_at ?? 0).getTime() - new Date((a as any).created_at ?? 0).getTime());
    return list;
  }

  // Client-side fallback when Algolia is not configured
  const clientFiltered = sortProducts(
    products.filter((p) => {
      const matchesCat = cat === "All" || p.category === cat;
      const term = search.toLowerCase();
      const matchesSearch = !term || p.title.toLowerCase().includes(term) || p.brand.toLowerCase().includes(term);
      return matchesCat && matchesSearch;
    }),
  );

  const algoliaIndex = (import.meta.env.VITE_ALGOLIA_INDEX as string | undefined) || "products";
  const usingMainIndexForSort = algoliaEnabled && algoliaIndexForSort(sort) === algoliaIndex;
  const algoliaHits = algoliaResult?.hits ?? [];
  const algoliaSorted = usingMainIndexForSort ? sortProducts(algoliaHits) : algoliaHits;

  const totalClientPages = Math.max(1, Math.ceil(clientFiltered.length / PAGE_SIZE));
  const clientPageSlice = clientFiltered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const filtered: Product[] = algoliaEnabled ? algoliaSorted : clientPageSlice;
  const totalPages = algoliaEnabled ? (algoliaResult?.nbPages ?? 1) : totalClientPages;
  const loading = algoliaEnabled ? (algoliaLoading || algoliaFetching) : isLoading;

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(query);
    setDebounced(query.trim());
  }

  function switchCategory(c: Cat) {
    setCat(c);
    if (!algoliaEnabled) {
      setQuery("");
      setSearch("");
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-6 pt-12 pb-24">
      <Reveal>
        <p className="editorial-eyebrow">The Feed</p>
        <h1 className="mt-2 font-serif text-5xl">
          <FlipWords
            words={["Discover", "Explore", "Browse", "Find", "Shop"]}
            className="italic shimmer"
            duration={3200}
          />
        </h1>
        <TextGenerateEffect
          words="A vibe-first index of every product across our creator storefronts."
          className="mt-3 max-w-xl text-muted-foreground"
          duration={0.4}
          staggerDelay={0.04}
        />
      </Reveal>

      <form onSubmit={handleSearch} className="mt-10 flex items-center gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search products or brands"
          className="flex-1 border border-border bg-card/80 backdrop-blur px-4 py-2.5 text-sm outline-none transition-colors focus:border-foreground"
        />
        <button
          type="submit"
          aria-label="Search"
          className="inline-flex items-center gap-1.5 border border-border bg-card/80 px-4 py-2.5 text-sm uppercase tracking-widest text-foreground transition-all duration-300 hover:bg-foreground hover:text-background"
        >
          <Search className="h-4 w-4" />
          <span className="hidden sm:inline">Search</span>
        </button>
      </form>

      <div className="mt-6 flex flex-wrap gap-2 border-b border-border pb-4">
        {categories.map((c) => (
          <button key={c} onClick={() => switchCategory(c)} className={`rounded-full border px-4 py-1.5 text-xs tracking-wide transition-all duration-300 ${cat === c ? "border-foreground bg-foreground text-background scale-105" : "border-border hover:bg-foreground hover:text-background hover:scale-105"}`}>
            {c}
          </button>
        ))}
        <div className="ml-auto flex flex-wrap gap-2">
          {sorts.map((s) => (
            <button
              key={s}
              onClick={() => setSort(s)}
              className={`rounded-full border px-3 py-1.5 text-[11px] tracking-wide transition-all duration-300 ${sort === s ? "border-foreground bg-foreground text-background" : "border-border hover:bg-foreground hover:text-background"}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {algoliaEnabled && debounced.length > 1 && creatorsLoadingOrFetching && (
        <section className="mt-10">
          <div className="editorial-eyebrow h-4 w-20 animate-pulse bg-muted" />
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex flex-col items-center gap-2 border border-border bg-card/60 p-4">
                <div className="h-16 w-16 animate-pulse rounded-full bg-muted" />
                <div className="flex flex-col items-center gap-1">
                  <div className="h-4 w-20 animate-pulse bg-muted" />
                  <div className="h-3 w-12 animate-pulse bg-muted" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {algoliaEnabled && debounced.length > 1 && creatorsError && !creatorsLoadingOrFetching && (
        <section className="mt-10">
          <div className="flex items-center justify-between border border-border bg-card/60 p-4">
            <div>
              <p className="text-sm font-medium">Creators search failed</p>
              <p className="text-xs text-muted-foreground">{(creatorsError as Error)?.message ?? "Something went wrong."}</p>
            </div>
            <button
              onClick={() => refetchCreators()}
              className="inline-flex items-center gap-1.5 border border-border px-4 py-2 text-xs uppercase tracking-widest transition-all hover:bg-foreground hover:text-background"
            >
              Retry
            </button>
          </div>
        </section>
      )}

      {algoliaEnabled && creatorHits.length > 0 && (
        <section className="mt-10">
          <p className="editorial-eyebrow">Creators</p>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {creatorHits.map((c) => (
              <Link
                key={c.objectID}
                to="/creator/$handle"
                params={{ handle: c.handle ?? c.objectID }}
                className="group flex flex-col items-center gap-2 border border-border bg-card/60 p-4 transition-all hover:border-foreground hover:bg-card"
              >
                {c.avatar_url ? (
                  <img src={c.avatar_url} alt={c.name ?? c.handle ?? ""} className="h-16 w-16 rounded-full object-cover" />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-muted" />
                )}
                <div className="text-center">
                  <p className="font-serif text-sm">{c.name ?? c.handle}</p>
                  {c.handle && <p className="text-[11px] text-muted-foreground">@{c.handle}</p>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-12 grid grid-cols-2 gap-x-6 gap-y-14 md:grid-cols-3 lg:grid-cols-4">
        {loading ? (
          <>
            <ProductSkeleton />
            <ProductSkeleton />
            <ProductSkeleton />
            <ProductSkeleton />
            <ProductSkeleton />
            <ProductSkeleton />
            <ProductSkeleton />
            <ProductSkeleton />
          </>
        ) : (
          filtered.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 90}>
              <ProductCard product={p} creatorHandle={p.creator_handle} creatorName={p.creator_name} />
            </Reveal>
          ))
        )}
        {!loading && filtered.length === 0 && (
          <div className="col-span-full">
            <Reveal>
              <div className="mx-auto max-w-md py-24 text-center">
                {query === "" && search === "" && cat === "All" ? (
                  <>
                    <p className="editorial-eyebrow">Coming soon</p>
                    <h2 className="mt-3 font-serif text-4xl">Nothing here yet</h2>
                    <p className="mt-4 text-sm text-muted-foreground">
                      Products will appear here once creators go live.
                    </p>
                    <Link
                      to="/creators"
                      className="mt-8 inline-flex items-center border border-foreground/80 px-5 py-2.5 text-xs uppercase tracking-widest text-foreground transition-colors hover:bg-foreground hover:text-background"
                    >
                      Meet the creators
                    </Link>
                  </>
                ) : (
                  <>
                    <p className="font-serif text-xl">No products match your search.</p>
                    <p className="mt-2 text-sm text-muted-foreground">Try a different term or category.</p>
                  </>
                )}
              </div>
            </Reveal>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-10 flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
            className="inline-flex items-center gap-1 border border-border px-3 py-2 text-xs uppercase tracking-widest transition-all hover:bg-foreground hover:text-background disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Prev</span>
          </button>

          {Array.from({ length: totalPages }, (_, i) => i)
            .filter((i) => i === 0 || i === totalPages - 1 || Math.abs(i - page) <= 1)
            .reduce<(number | string)[]>((acc, i, idx, arr) => {
              if (idx > 0 && (arr[idx - 1] as number) !== i - 1) acc.push("…");
              acc.push(i);
              return acc;
            }, [])
            .map((p, i) =>
              typeof p === "string" ? (
                <span key={`ellipsis-${i}`} className="px-2 text-xs text-muted-foreground">…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  disabled={loading}
                  className={`min-w-[2.5rem] border px-3 py-2 text-xs uppercase tracking-widest transition-all disabled:opacity-30 ${page === p ? "border-foreground bg-foreground text-background" : "border-border hover:bg-foreground hover:text-background"}`}
                >
                  {p + 1}
                </button>
              )
            )}

          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1 || loading}
            className="inline-flex items-center gap-1 border border-border px-3 py-2 text-xs uppercase tracking-widest transition-all hover:bg-foreground hover:text-background disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-foreground"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
